import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { forkJoin } from 'rxjs';
import { AppIcons } from '../../../../shared/app.icons';
import { AppSettings } from '../../../../shared/app.settings';
import { MITableComponent } from '../../../../shared/components/mi-table/mi-table.component';
import type { Country } from '../../../../shared/models/country';
import type { Language } from '../../../../shared/models/language';
import { CommonBindingDataService } from '../../../../shared/services/common-binding-data.service';
import { ConfigService } from '../../../../shared/services/config.service';
import type { Column, AttributeData, Field } from '../../../../shared/models/attribute.models';
import type { EntityList } from '../../../../shared/models/entity.models';
import type { ListResponse, Values, Pagination } from '../../../../shared/models/listResponse.models';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { AddProtocolComponent } from "./add-protocol/add-protocol.component";
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { CommonModule } from '@angular/common';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import dayjs from 'dayjs';

interface FilterField {
  attributeCode: string;
  attributeValue: string;
  isDisable: boolean;
  presetValues: Array<{ labelValue: string; labelKey: string }>;
}

interface TableRow {
  id: string;
  driverGroup: string;
  active?: string;
  deleted?: boolean;
  [key: string]: unknown;
}

interface SortColumnDetails {
  attributeId: string;
  sortOrder: number;
}

interface ExtendedColumn extends Column {
  attributeCode: string;
  attributeIndex: number;
}

@Component({
  selector: 'app-protocol-details',
  templateUrl: './protocol-details.component.html',
  styleUrl: './protocol-details.component.scss',
  standalone: true,
  imports: [TabViewModule, TranslateModule, MITableComponent, BreadcrumbModule, ButtonModule, DialogModule, AddProtocolComponent, CommonModule, AccessProviderDirective]
})
export class ProtocolDetailsComponent implements OnInit {
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  @Output() saveSuccess = new EventEmitter<void>();
  @Output() emitTotalRecords = new EventEmitter();
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns: ExtendedColumn[] = [];
  cols: ExtendedColumn[] = [];
  @Input() moduleName: string;
  @Input() sponsorRelatedData: Record<string, unknown>;
  value: number = 50;
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels: Record<string, string> = {};
  attributeLabelsData: Record<string, unknown> = {};
  filterAttributeLabels: Record<string, string>;
  labelData = {
    attributeLabels: [
      { labelKey: 'manage_users_grid.fields.Status.label', labelValue: 'Status' },
      { labelKey: 'manage_users_grid.fields.UserRole.label', labelValue: 'User Role' }
    ]
  };
  listColumns: Values;
  allColumns: ExtendedColumn[];
  pagination: Pagination;
  totalRecords: number = 0;
  tableData: TableRow[] = [];
  buttons: Array<Record<string, unknown>> = [];
  country: Country;
  language: Language;
  entityData: any = {
    actionStatus: "",
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    status: 'both',
    tableViewCode: AppSettings.VIEW_CODE.PROTOCOL_LIST_IN_SPONSOR_VIEW
  };
  routePath: MenuItem[] = [];
  home: MenuItem = {};
  isModal: boolean = false;
  selectedColumns!: Column[];
  attributeData: AttributeData;
  data: AttributeData;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  columnsData: ExtendedColumn[];
  bulkDeleteLabel: string = this.cs.getLabel('manage_users.delete');
  bulkUpdateLabel: string = this.cs.getLabel('manage_users.lbl_update_user');
  bulkUpdateHeader: string;
  reviewHeader: string;
  entityNameToDisplay: string;
  btnUpdate: string;
  deleteRoleHeaderMsg: string;
  deleteRoleMsg: string;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  filterFieldsArray: FilterField[];
  isEntity: boolean = false;
  entityName: string;
  currentRow: TableRow;
  filterList: ExtendedColumn[];
  entityDataForRole: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: '',
    actionStatus: '',
    status: 'both',
  };
  miIcons = AppIcons;
  showAddDialog: boolean = false;
  sponsorId: string;
  protocolId: string;
  sponsorAttributeId: number;
  startDateAttributeId: number;
  endDateAttributeId: number;

  constructor(
    private router: Router,
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private configService: ConfigService,
    private cd: ChangeDetectorRef,
    private permissionAccessService: PermissionAccessService,
    private loaderService: LoaderService,
    private entityService: EntityService,
    private commonBindingService: CommonBindingDataService,
    private appSettings: AppSettings,
    private restApiService: RestApiService,
    private metaDataService: MetaDataService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.retrieveSponsorId();
    this.setUpRoutePath();
    this.setUpRowItems();
    this.setupBulkSelectionsItems();
    this.initializeOtherVariablesAndAPI();
    this.getMetaData();
  }

  retrieveSponsorId() {
    this.route.queryParams.subscribe(params => {
      this.sponsorId = params?.id;
    });
  }

  getMetaData() {
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res: Record<string, { responseBody: string }>) => {

      const advancedFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advancedFilter) {
        this.sponsorAttributeId = this.cs.getAttributeId('sponsor_entity_id', advancedFilter);
        this.setProtocolFilterView(advancedFilter);

      }

      const sort = JSON.parse(res.sort.responseBody);
      const startDate = sort.tabs[0].groups[0].fields.find(field => field.attributeCode === 'start_date');
      this.startDateAttributeId = startDate.attributeId;
      const endDate = sort.tabs[0].groups[0].fields.find(field => field.attributeCode === 'end_date');
      this.endDateAttributeId = endDate.attributeId;

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setProtocolTableView(defaultTableView);
      }
      this.cs.applyFilterToColumn(defaultTableView, sort)
      this.getData();

    });
  }

  private getMultiApiPayload() {
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW}&tableViewCode=${AppSettings.VIEW_CODE.PROTOCOL_LIST_IN_SPONSOR_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
    ]
  }

  setProtocolFilterView(filterResponse: AttributeData) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse) as unknown as Record<string, string>;
    this.filterList = this.cs.getColumns(filterResponse) as unknown as ExtendedColumn[];
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');

    this.filterList = this.filterList.filter(filter =>
      filter.attributeCode !== 'end_date' && filter.attributeCode !== 'sponsor_entity_id');

    this.filterList = this.filterList.map((filter: any) => {
      if (filter.attributeCode === 'start_date') {
        filter.inputCode = 'dateRange';
      }
      if (filter.attributeCode === 'sponsor_entity_id') {
        filter.inputCode = 'entitySelectSearch';
        filter.moduleName = 'sponsor';
      }
      return filter;
    });
  }

  setProtocolTableView(res: AttributeData) {
    this.isLoading = false;
    this.data = res;
    this.attributeLabels = this.cs.getAttributeLabels(this.data) as unknown as Record<string, string>;
    this.allColumns = this.cs.getColumns(this.data).filter(column =>
      !['entity_created_at'].includes(column.attributeCode)
    );
    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `protocol.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
    });
    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  initializeOtherVariablesAndAPI() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.btnLabel = this.cs.getLabel('protocol.lbl_add_new_protocol');
    this.bulkDeleteLabel = this.cs.getLabel('manage_users.message.delete_user');
    this.bulkUpdateLabel = this.cs.getLabel('manage_users.lbl_update_user');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));

    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.bulkUpdateHeader = this.cs.getLabel('manage_users.lbl_bulk_update_header');
    this.entityNameToDisplay = this.cs.getLabel('manage_users.lbl_entity_name_display');
    this.btnUpdate = this.cs.getLabel('manage_users.btn_bulk_update');
    this.reviewHeader = this.cs.getLabel('manage_users.lbl_review_users');
    this.deleteRoleMsg = this.cs.getLabel('manage_users.message.confirm_delete_msg');
    this.deleteRoleHeaderMsg = this.cs.getLabel('manage_users.message.delete_user');
    this.getBulkUpdateAttributeData();
  }

  private setUpRoutePath() {
    this.routePath = [
      { label: this.cs.getLabel('manage_users.lbl_route_path') },
    ];
  }

  private setUpRowItems() {
    const isEdit = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'ALL', 'ADD');
    this.rowItems = [
      ...(isEdit ? [{
        label: this.cs.getLabel('manage_users.edit'),
        icon: `${AppIcons.EDIT_2} wh-122`,
        iconClass: 'bulk-update-icon',
        command: () => {
          this.update();
        }
      }] : [])
    ];
  }

  private setupBulkSelectionsItems() {
    const isBulkAction = this.permissionAccessService.hasAccess('USER_MANAGEMENT', 'MANAGE_USERS', 'DETAILS', 'BULK_ACTION');
    this.bulkSelectionsItems = [
      ...(isBulkAction ? [{
        label: this.bulkUpdateLabel,
        icon: `${AppIcons.EDIT_2} wh-122`,
        iconClass: 'bulk-update-icon',
        styleClass: 'bulk-update-list',
        command: (e: { item: { label: string } }) => {
          if (e.item.label === this.bulkUpdateLabel) {
            this.openBulkUpdateDialog();
          }
        },
      }] : []),
      ...(isBulkAction ? [{
        label: this.bulkDeleteLabel,
        icon: `${AppIcons.BASIC_TRAY} wh-122`,
        iconClass: 'bulk-update-icon',
        styleClass: 'bulk-update-list',
        command: () => {
          this.tableRef.delete(AppSettings.DELETE_TYPE.BULK);
        },
      }] : []),
    ];
  }

  private openBulkUpdateDialog() {
    this.visible = true;
    this.showBulkUpdateDialog = false;
    setTimeout(() => {
      this.showBulkUpdateDialog = true;
      this.tableRef.showBulkRowDialog();
    }, 200);
  }

  getBulkUpdateAttributeData() {
    this.attributeData = {
      attributeLabels: [],
      tabs: [
        {
          tabCode: 'bulk_update',
          tabIndex: 0,
          groups: [
            {
              code: 'status_group',
              index: 0,
              fields: [
                {
                  attributeId: 1,
                  attributeCode: 'user_status',
                  attributeIndex: 0,
                  attributeValue: null,
                  presetValues: [
                    { labelValue: 'Active', labelKey: 'Active', parentLabelKey: null },
                    { labelValue: 'Inactive', labelKey: 'Inactive', parentLabelKey: null },
                  ],
                  validation: null,
                  uiColumns: 1,
                  inputCode: 'select',
                  inputUnitCode: null,
                  multi: false,
                  tableColumnIndex: 0,
                  filterIndex: 0,
                  encrypted: false,
                  dependsOnAttributeId: 0
                }
              ],
              entityRelationshipConfigId: null
            }
          ]
        }
      ],
      entityCode: 'protocol',
      tenantId: '',
      languageId: '',
      countryId: '',
      relations: [],
      relatedAttributes: []
    };
    this.updateFilterFieldsArray(this.attributeData.tabs[0]?.groups[0]?.fields);
  }

  private updateFilterFieldsArray(fields: Field[]) {
    if (!fields) return;

    this.filterFieldsArray = [];
    fields.forEach(field => {
      if (field.attributeCode === 'user_status') {
        this.filterFieldsArray.push({
          attributeCode: field.attributeCode,
          attributeValue: 'Status',
          isDisable: false,
          presetValues: field.presetValues?.map(pv => ({ labelValue: pv.labelValue, labelKey: pv.labelKey })) || []
        });
      }
    });
  }

  getTableView() {
    this.entityService.getAttributeDefinition(AppSettings.ENTITY_CODE.PROTOCOL, AppSettings.VIEW_CODE.PROTOCOL_LIST_IN_SPONSOR_VIEW, false).subscribe(res => {
      if (res) {
        this.searchEntity();
      }
    });
  }

  getData() {
    return forkJoin({
      tableViewData: this.getTableView()
    })
  }

  searchEntity() {
    this.tableData = [];
    this.entityData.countryCode = this.country[0].countryCode;
    const isSponsorIdFilter = this.entityData.filters.some(ele => ele.attributeId === this.sponsorAttributeId);
    if (!isSponsorIdFilter) {
      this.entityData.filters.push({
        attributeId: this.sponsorAttributeId,
        attributeValue: this.sponsorId
      });
    }
    this.entityService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL, this.entityData, false).subscribe((res: ListResponse) => {
      this.listColumns = res?.data[0]?.values;
      this.pagination = res?.pagination;
      this.totalRecords = res?.count;
      this.emitTotalRecords.emit(res?.count);
      res.data?.forEach(data => {
        this.tableData.push({
          id: data.id,
          driverGroup: data.relatedData?.map(ele => {
            return `${ele.values.first_name} ${ele.values.last_name}`
          }).join(', '),
          ...data.values,
        });
      });
      this.loaderService.hide();
    });
  }

  onSearchValueChanges(event: string) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  setAdvanceFilter(key: string, value: unknown) {
    if (value) {
      if (key === 'start_date') {
        this.entityData.advanceFilters = this.entityData.advanceFilters.filter(
          (filter: any) => filter.attributeId !== this.startDateAttributeId
        );
        const endDate = dayjs(value[1]).endOf('day').valueOf() ? dayjs(value[1]).endOf('day').valueOf() : dayjs(value[0]).startOf('day').valueOf();
        this.entityData.advanceFilters.push({
          attributeId: Number(this.startDateAttributeId),
          fromValue: this.metaDataService.setDateFilterOffset(dayjs(value[0]).startOf('day').valueOf()),
          comparisonOperator: AppSettings.COMPARISON_OPERATOR.GTE
        }, {
          attributeId: Number(this.endDateAttributeId),
          fromValue: this.metaDataService.setDateFilterOffset(endDate),
          comparisonOperator: AppSettings.COMPARISON_OPERATOR.LTE
        });
      }
    }
  }

  onFilterValueChange(event: Record<string, unknown>) {
    this.entityData.advanceFilters = [];
    this.entityData.filters = [];
    const advanceFilterAttributeCodes = ['start_date', 'end_date'];
    const vData = event;
    for (const [key, value] of Object.entries(vData)) {
      const attributeValue = value;
      if (advanceFilterAttributeCodes.includes(key)) {
        this.setAdvanceFilter(key, value);
      } else if (attributeValue) {
        this.entityData.filters.push({
          attributeCode: key,
          attributeValue
        });
      }
    }

    if (event.Status && (event.Status as string[]).length === 2 && (event.Status as string[]).includes('Active') && (event.Status as string[]).includes('Inactive')) {
      this.entityData.status = 'both';
    } else {
      this.entityData.status = (event.Status as string)?.toString() ? (event.Status as string).toString() : 'both';
    }

    this.entityData.filters = _.filter(_.uniq(this.entityData.filters, (item) => {
      if (item.attributeValue && item.attributeValue.length > 0) {
        return item.attributeCode;
      }
    }), (element) => {
      if (element.attributeValue && element.attributeValue.length > 0) {
        return true;
      }
      return false;
    });

    this.entityData.offset = event.first as number;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.searchEntity();
  }

  onPageChange(event: { first: number; rows: number }) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event: number) {
    this.entityData.limit = event;
    this.searchEntity();
  }

  update() {
    this.onUpdateProtocol(this.currentRow.id);
  }

  onUpdateProtocol(userId: string) {
    this.protocolId = userId;
    this.showAddDialog = true;
  }

  makeProtocolActiveInactive(currentRow: TableRow) {
    const isActive = currentRow.active === AppSettings.USERS_STATUS.ACTIVE;
    const headerMsg = isActive ? this.cs.getLabel('manage_users.inactive_user_header_message') : this.cs.getLabel('manage_users.active_user_header_message');
    const bodyMsg = isActive ? this.cs.getLabel('manage_users.inactive_status_message') : this.cs.getLabel('manage_users.active_status_message')
    const acceptLabel = isActive ? this.cs.getLabel('manage_users.lbl_deactivate') : this.cs.getLabel('manage_users.lbl_activate');
    this.confirmationService.confirm({
      header: headerMsg,
      message: bodyMsg,
      acceptLabel,
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectLabel: this.cs.getLabel('manage_users.cancel'),
      rejectButtonStyleClass: 'bg-white text-color',
      acceptButtonStyleClass: isActive ? 'bg-red-500' : 'bg-green-500',
      accept: () => {
        this.tableRef.changeStatusOfEntity();
      },
      reject: () => {
      }
    });

  }

  onChangeStatus(currentRow: TableRow) {
    const newStatus = currentRow.active === AppSettings.USERS_STATUS.ACTIVE ? false : true;
    return newStatus;
  }

  private showSuccessMessage(labelKey: string) {
    this.messageService.add({
      key: 'tst',
      severity: 'success',
      summary: 'Successful',
      detail: this.cs.getLabel(labelKey),
    });
  }

  private showErrorMessage(labelKey: string) {
    this.messageService.add({
      key: 'tst',
      severity: 'error',
      summary: 'Error',
      detail: this.cs.getLabel(labelKey)
    });
  }

  setCurrentRowData(event: TableRow) {
    this.currentRow = event;
    this.setEditOptionDisabledForDeletedRecord(event);
  }

  setEditOptionDisabledForDeletedRecord(data: TableRow) {
    const status = Object.entries(data?.protocol_status)[0][0];
    if (this.rowItems && this.rowItems[0]) {
      this.rowItems[0].disabled = (status.toLowerCase() === 'completed' || status.toLowerCase() === 'inactive');
      this.cd.detectChanges();
    }
  }

  onStaticBulkDataUpdate() { }

  onBulkDataDeleteEvent() { }

  onAddUser() {
    this.showAddDialog = true;
  }

  hidePopup(event: boolean) {
    if (event) {
      this.saveSuccess.emit();
      this.searchEntity();
    }
    this.protocolId = null;
    this.showAddDialog = false;
  }

  onSortData(sortColumnDetails: SortColumnDetails) {
    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }
}
